import { X, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { claimDaily, dailyBonusFor, DAILY_POINTS, isDailyClaimable, peekDailyStreak } from "../utils/rewards";
import { toast } from "./ui/sonner";

/* live clock down to UTC midnight — the streak/claim window boundary */
const useMidnightCountdown = (active) => {
  const [left, setLeft] = useState("");
  useEffect(() => {
    if (!active) return undefined;
    const tick = () => {
      const now = new Date();
      const midnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
      const s = Math.max(0, Math.floor((midnight - now.getTime()) / 1000));
      const h = String(Math.floor(s / 3600)).padStart(2, "0");
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
      const sec = String(s % 60).padStart(2, "0");
      setLeft(`${h}:${m}:${sec}`);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return left;
};

export function DailyRation() {
  const [open, setOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const countdown = useMidnightCountdown(open && !claimed);

  useEffect(() => {
    if (!isDailyClaimable()) return undefined;
    let cancelled = false;
    let timer;
    // hold until the entry gate is out of the way
    const tryOpen = () => {
      if (cancelled) return;
      // the arena keeps its CTAs clear — no ration popup over the battle table
      if (window.location.pathname === "/tcg") return;
      if (sessionStorage.getItem("dolo-gate-access") === "granted") setOpen(true);
      else timer = window.setTimeout(tryOpen, 2000);
    };
    timer = window.setTimeout(tryOpen, 2600);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  const claim = async () => {
    setClaiming(true);
    try {
      const result = await claimDaily();
      if (!result) {
        setOpen(false);
        return;
      }
      setClaimed(result);
      toast.success(`BIO HACK TUBE CRACKED — +${result.earned} PTS.`);
      window.setTimeout(() => setOpen(false), 3800);
    } finally {
      setClaiming(false);
    }
  };

  if (!open) return null;

  const nextStreak = peekDailyStreak();
  const bonus = dailyBonusFor(nextStreak);
  const streakAtRisk = nextStreak > 1;

  return (
    <div className="daily-ration" data-testid="daily-ration-popup" role="dialog" aria-label="Daily free Bio Hack tube">
      <button type="button" className="daily-ration-close" onClick={() => setOpen(false)} data-testid="daily-ration-close" aria-label="Close daily ration">
        <X size={14} />
      </button>
      <div className={`daily-tube ${claimed ? "is-claimed" : ""}`} aria-hidden="true">
        <span className="daily-tube-cap" />
        <span className="daily-tube-glass">
          <span className="daily-tube-liquid" />
          <span className="daily-tube-bubble b1" />
          <span className="daily-tube-bubble b2" />
        </span>
        <span className="daily-tube-label">BH</span>
      </div>
      <div className="daily-ration-copy">
        <span className="daily-ration-eyebrow">DAILY RATION // FREE DROP</span>
        {claimed ? (
          <>
            <strong data-testid="daily-ration-status">TUBE CRACKED — ENERGY +{claimed.earned} PTS</strong>
            <p>
              Stash now {claimed.total} PTS · tubes stocked: {claimed.tubes}.
            </p>
            <p className="daily-ration-streak" data-testid="daily-ration-streak">
              DAILY STREAK: {claimed.streak} DAY{claimed.streak > 1 ? "S" : ""} — claim tomorrow for day {claimed.streak + 1} (+
              {DAILY_POINTS + dailyBonusFor(claimed.streak + 1)} PTS).
            </p>
          </>
        ) : (
          <>
            <strong>FREE BIO HACK TUBE</strong>
            <p>One free tube a day to boost your energy levels. Crack it for +{DAILY_POINTS + bonus} PTS on your rewards stash.</p>
            {streakAtRisk ? (
              <p className="daily-ration-streak" data-testid="daily-ration-streak-teaser">
                STREAK DAY {nextStreak} — CONSECUTIVE CLAIM BONUS +{bonus} PTS ON TOP
              </p>
            ) : null}
            <p className={`daily-ration-countdown ${streakAtRisk ? "is-urgent" : ""}`} data-testid="daily-ration-countdown">
              {streakAtRisk ? `CLAIM WITHIN ${countdown} TO KEEP YOUR ${nextStreak - 1}-DAY STREAK` : `TODAY'S RATION EXPIRES IN ${countdown}`}
            </p>
            <button type="button" className="daily-ration-claim" onClick={claim} disabled={claiming} data-testid="daily-ration-claim-button">
              <Zap size={14} /> {claiming ? "CRACKING…" : `CLAIM TUBE — +${DAILY_POINTS + bonus} PTS`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
